import { BaseModal } from "@/react/components/editors/base/BaseModal";
import { BaseModalBody } from "@/react/components/editors/base/BaseModalBody";
import { MyMediaProvider, useMyMediaContext } from "./context/MyMediaContext";
import { SearchBar } from "./components/SearchBar";
import { FileGrid } from "./components/FileGrid";
import { AssignmentFooter } from "./components/AssignmentFooter";
import { ViewToggle } from "./components/ViewToggle";
import { SortDropdown } from "./components/SortDropdown";
import { FileTypeFilter } from "./components/FileTypeFilter";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { EntityRecord } from "@/types/entity";
import { useMemo, useCallback } from "react";
import { VIEW_TYPES } from "@/other/constants/OtherKeys";
import { SETTING_KEYS } from "@/react/components/entityViews/settings/constants/settingsStructure";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/modern/components/ui/dialog";
import { ENTITY_CONFIGS } from "@/other/constants/EntityManagerConfig";
import { FileCountDisplay } from "./components/FileCountDisplay";
import { UploadButton } from "./components/UploadButton";
import { AnalyzeDialog } from "@/react/components/entityViews/files/components/editors/AnalyzeFile/AnalyzeDialog";
import type { FormattedFile } from "@/react/components/entityViews/files/context/EntityFileContext";
import { Check, X } from "lucide-react";
import { Button } from "../../ui/button";

interface MyMediaPopupProps {
  record?: {
    entityRecord?: EntityRecord;
    entityType?: string;
    [key: string]: any;
  } | null;
  onClose: () => void;
  editorInstance?: {
    className?: string;
    [key: string]: unknown;
  };
}

const InsightSetupDialogs = ({ onClose }: { onClose: () => void }) => {
  const {
    insightSetup,
    dismissInsightSetup,
  } = useMyMediaContext();
  const { viewManager, selectedBoard } = useSwivuBryntumContext();

  const handleGoToOpenAISettings = useCallback(() => {
    if (!selectedBoard?.record) return;
    viewManager?.openAnonymousViewByType({
      viewType: VIEW_TYPES.settings,
      viewName: "Settings",
      otherParams: {
        setting: SETTING_KEYS.INTEGRATION_OPENAI,
      },
    });
    dismissInsightSetup();
    onClose();
  }, [selectedBoard, viewManager, dismissInsightSetup, onClose]);

  const noAiBoardsOpen = insightSetup?.mode === "no-ai-boards";
  const hasBoard = Boolean(selectedBoard?.record);

  return (
    <Dialog
      open={noAiBoardsOpen}
      onOpenChange={(next) => {
        if (!next) dismissInsightSetup();
      }}
    >
      <DialogContent
        className="z-[--swivu-bryntum-z-index] gap-0 p-0 sm:max-w-[400px]"
        showCloseButton
        closeButtonClassName="right-3 top-3"
      >
        <DialogHeader className="space-y-3 border-0 p-0 px-6 pb-1 pt-6 pr-14 text-left">
          <DialogTitle className="text-base font-semibold leading-snug">No AI-enabled boards</DialogTitle>
          <DialogDescription className="text-sm leading-relaxed">
            <span className="block text-muted-foreground">
              Add an OpenAI API key in board settings to use AI insights on unassigned files.
            </span>
            {!hasBoard && (
              <span className="mt-2.5 block text-muted-foreground">
                Open a board first—we&apos;ll open that board&apos;s OpenAI settings.
              </span>
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="border-t border-border/60 px-6 py-4">
          <Button type="button" className="w-full" onClick={handleGoToOpenAISettings} disabled={!hasBoard}>
            Open OpenAI settings
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const MyMediaPopupContent = ({ onClose, editorInstance }: { onClose: () => void; editorInstance?: MyMediaPopupProps["editorInstance"] }) => {
  const {
    files,
    selectedFiles,
    selectAllFiles,
    clearSelection,
    entityRecord,
    insightDialogFile,
    closeInsightDialog,
    defaultBoardId,
    aiEnabledBoards,
    isLoadingBoards,
    setDefaultBoard,
  } = useMyMediaContext();

  const allSelected = files.length > 0 && selectedFiles.size === files.length;
  const hasEntity = entityRecord !== null;

  const insightDialogFormattedFile = useMemo((): FormattedFile | null => {
    if (!insightDialogFile) return null;
    return {
      id: insightDialogFile.id,
      name: insightDialogFile.name,
      thumbnailUrl: insightDialogFile.thumbnail_url,
      originalImageUrl: insightDialogFile.url,
      canEdit: true,
      canView: true,
      record: null,
      isCCImage: false,
    };
  }, [insightDialogFile]);

  return (
    <BaseModal
      record={{}}
      onClose={onClose}
      title="My Media"
      description="Manage all your media files"
      editorInstance={editorInstance}
      hideMenu={true}
      size={{
        width: "80vw",
        height: "80vh",
        maxHeight: "100vh",
        maxWidth: "100vw",
        minWidth: "min(70rem, 100vw)",
        minHeight: "min(55rem, 100vh)",
      }}
      modal={false}
      version={undefined}
      swivuAssistanceConfig={undefined}
      headerIconCls=""
    >
      <BaseModalBody>
        <div className="flex flex-col h-full">
          <div className="flex-shrink-0 pb-6 border-b border-border/50 mb-6 space-y-4">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center justify-between gap-4">
              <SearchBar />
            </div>
              <div className="flex items-center gap-3">
                <FileCountDisplay />
                <ViewToggle />
                <SortDropdown />
                <FileTypeFilter />
                {files.length > 0 && hasEntity && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={allSelected ? clearSelection : selectAllFiles}
                  >
                    {allSelected ? (
                      <>
                        <X className="h-4 w-4" />
                        Unselect all
                      </>
                    ) : (
                      <>
                        <Check className="h-4 w-4" />
                        Select all
                      </>
                    )}
                  </Button>
                )}
                <UploadButton />
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-auto min-h-0">
            <FileGrid />
          </div>
        </div>
      </BaseModalBody>
      <AssignmentFooter />
      <InsightSetupDialogs onClose={onClose} />
      {insightDialogFile && insightDialogFormattedFile && (
        <AnalyzeDialog
          open
          onOpenChange={(next) => {
            if (!next) closeInsightDialog();
          }}
          file={insightDialogFormattedFile}
          unassignedFileId={insightDialogFile.id}
          insightBoardId={defaultBoardId ?? undefined}
          unassignedAiBoards={aiEnabledBoards}
          unassignedBoardsLoading={isLoadingBoards}
          onUnassignedDefaultBoardChange={setDefaultBoard}
        />
      )}
    </BaseModal>
  );
};

export const MyMediaPopup = ({ record, onClose, editorInstance }: MyMediaPopupProps) => {
  const { entityManager, project } = useSwivuBryntumContext();
  const { entityRecord: paramEntityRecord, entityType: paramEntityType } = record || {};

  /* We have to calculate & pass entity record */
  const entityRecord = useMemo(() => {
    if (paramEntityRecord) {
      return paramEntityRecord;
    }

    if (entityManager?.isOpen && entityManager?.entityType && entityManager?.entityId) {
      const entityType = entityManager.entityType;
      const entityId = entityManager.entityId;
      const config = ENTITY_CONFIGS[entityType];
      if (config) {
        const entity = project.getCrudStore(config.storeKey as string).getById(Number(entityId));
        if (entity) {
          return entity as unknown as EntityRecord;
        }
      }
    }

    return null;
  }, [paramEntityRecord, paramEntityType, entityManager?.isOpen, project]);

  return (
    <MyMediaProvider initialEntityRecord={entityRecord}>
      <MyMediaPopupContent onClose={onClose} editorInstance={editorInstance} />
    </MyMediaProvider>
  );
};
